/**
 * Internal Employees Management
 * Manages internal employees with levels, contracts, and invitations
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, holdingAdminNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Search,
  Mail,
  Phone,
  MapPin,
  FileText,
  Send,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Loader2,
  Edit,
  Trash2,
  Link as LinkIcon,
  DollarSign,
  Briefcase,
  Eye,
  Download,
  HardHat,
  Crown,
  Building2,
  Package,
  ClipboardList,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Position icons mapping
const POSITION_ICONS = {
  scaffold_l1: HardHat,
  scaffold_l2: HardHat,
  scaffold_l3: HardHat,
  team_leader: Crown,
  foreman: ClipboardList,
  warehouse: Package,
  project_leader: Building2,
};

// Status colors
const STATUS_COLORS = {
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  invited: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  active: "bg-green-500/20 text-green-400 border-green-500/30",
  contract_signed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  terminated: "bg-red-500/20 text-red-400 border-red-500/30",
};

const STATUS_LABELS = {
  pending: "Venter",
  invited: "Invitert",
  active: "Aktiv",
  contract_signed: "Kontrakt signert",
  terminated: "Avsluttet",
};

export default function HoldingInternalEmployees() {
  const { token, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPosition, setFilterPosition] = useState("all");

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    personal_id: "",
    position_key: "scaffold_l1",
    hourly_rate: 230,
    contract_type: "Permanent",
    start_date: "",
    place_of_work: "Oslo and surrounding areas",
    work_hours: "07:00–15:00",
    notes: "",
  });

  // Platform user search
  const [searchPlatformUsers, setSearchPlatformUsers] = useState("");
  const [platformUsers, setPlatformUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  useEffect(() => {
    fetchPositions();
    fetchEmployees();
  }, [token]);

  const fetchPositions = async () => {
    try {
      const res = await axios.get(`${API}/internal-employees/positions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPositions(res.data.positions || []);
    } catch (error) {
      console.error("Failed to fetch positions:", error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/internal-employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(res.data.employees || []);
    } catch (error) {
      toast.error("Kunne ikke hente ansatte");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Navn og e-post er påkrevd");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/internal-employees`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ansatt opprettet!");
      setShowAddModal(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke opprette ansatt");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEmployee = async () => {
    if (!selectedEmployee) return;

    setSaving(true);
    try {
      await axios.put(
        `${API}/internal-employees/${selectedEmployee.employee_id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Ansatt oppdatert!");
      setShowEditModal(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke oppdatere ansatt");
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvitation = async (employee) => {
    try {
      await axios.post(
        `${API}/internal-employees/${employee.employee_id}/invite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invitasjon sendt til ${employee.email}!`);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke sende invitasjon");
    }
  };

  const handleDeleteEmployee = async (employee) => {
    if (!window.confirm(`Er du sikker på at du vil slette ${employee.name}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/internal-employees/${employee.employee_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ansatt slettet");
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke slette ansatt");
    }
  };

  const searchPlatformUsersHandler = async (query) => {
    if (query.length < 2) {
      setPlatformUsers([]);
      return;
    }

    setSearchingUsers(true);
    try {
      const res = await axios.get(
        `${API}/internal-employees/search/platform-users?query=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlatformUsers(res.data.users || []);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleInvitePlatformUser = async (platformUser) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/internal-employees/invite-platform-user`,
        {
          platform_user_id: platformUser.user_id,
          position_key: formData.position_key,
          hourly_rate: formData.hourly_rate,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${platformUser.name} lagt til som intern ansatt!`);
      setShowInviteModal(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke invitere bruker");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      personal_id: "",
      position_key: "scaffold_l1",
      hourly_rate: 230,
      contract_type: "Permanent",
      start_date: "",
      place_of_work: "Oslo and surrounding areas",
      work_hours: "07:00–15:00",
      notes: "",
    });
  };

  const openEditModal = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      name: employee.name || "",
      email: employee.email || "",
      phone: employee.phone || "",
      address: employee.address || "",
      personal_id: employee.personal_id || "",
      position_key: employee.position_key || "scaffold_l1",
      hourly_rate: employee.hourly_rate || 230,
      contract_type: employee.contract_type || "Permanent",
      start_date: employee.start_date || "",
      place_of_work: employee.place_of_work || "",
      work_hours: employee.work_hours || "",
      notes: employee.notes || "",
    });
    setShowEditModal(true);
  };

  const openContractModal = (employee) => {
    setSelectedEmployee(employee);
    setShowContractModal(true);
  };

  const handlePositionChange = (key) => {
    const position = positions.find((p) => p.key === key);
    setFormData({
      ...formData,
      position_key: key,
      hourly_rate: position?.default_rate || formData.hourly_rate,
    });
  };

  // Filter employees
  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch =
      !searchQuery ||
      emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = filterStatus === "all" || emp.status === filterStatus;
    const matchesPosition = filterPosition === "all" || emp.position_key === filterPosition;

    return matchesSearch && matchesStatus && matchesPosition;
  });

  // Stats
  const stats = {
    total: employees.length,
    active: employees.filter((e) => e.status === "active" || e.status === "contract_signed").length,
    pending: employees.filter((e) => e.status === "pending" || e.status === "invited").length,
    contractsSigned: employees.filter((e) => e.contract_signed).length,
  };

  return (
    <DashboardLayout
      title="Interne Ansatte"
      navItems={holdingAdminNavItems}
      role="holding_admin"
    >
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-[#94A3B8]">Totalt ansatte</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.active}</p>
                <p className="text-xs text-[#94A3B8]">Aktive</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.pending}</p>
                <p className="text-xs text-[#94A3B8]">Venter</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.contractsSigned}</p>
                <p className="text-xs text-[#94A3B8]">Signerte kontrakter</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions & Filters */}
      <Card className="bg-[#0A0A0A] border-white/10 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Søk etter navn eller e-post..."
                  className="pl-10 bg-black/50 border-white/10 text-white"
                />
              </div>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[160px] bg-black/50 border-white/10 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="all">Alle statuser</SelectItem>
                  <SelectItem value="pending">Venter</SelectItem>
                  <SelectItem value="invited">Invitert</SelectItem>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="contract_signed">Kontrakt signert</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterPosition} onValueChange={setFilterPosition}>
                <SelectTrigger className="w-[200px] bg-black/50 border-white/10 text-white">
                  <SelectValue placeholder="Stilling" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="all">Alle stillinger</SelectItem>
                  {positions.map((pos) => (
                    <SelectItem key={pos.key} value={pos.key}>
                      {pos.title_no}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => setShowInviteModal(true)}
                variant="outline"
                className="border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/10"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Inviter fra plattform
              </Button>
              <Button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#23F7DD]/80"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Legg til manuelt
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#23F7DD]" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
              <p className="text-[#94A3B8]">Ingen ansatte funnet</p>
              <p className="text-sm text-[#64748B] mt-1">
                Legg til din første interne ansatt
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-[#94A3B8]">Ansatt</TableHead>
                  <TableHead className="text-[#94A3B8]">Stilling</TableHead>
                  <TableHead className="text-[#94A3B8]">Timesats</TableHead>
                  <TableHead className="text-[#94A3B8]">Status</TableHead>
                  <TableHead className="text-[#94A3B8]">Kontrakt</TableHead>
                  <TableHead className="text-[#94A3B8] text-right">Handlinger</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((employee) => {
                  const PositionIcon = POSITION_ICONS[employee.position_key] || HardHat;
                  return (
                    <TableRow
                      key={employee.employee_id}
                      className="border-white/10 hover:bg-white/5"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                            <span className="text-[#23F7DD] font-bold">
                              {employee.name?.charAt(0)?.toUpperCase() || "?"}
                            </span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{employee.name}</p>
                            <p className="text-sm text-[#64748B]">{employee.email}</p>
                          </div>
                          {employee.platform_user_id && (
                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                              <LinkIcon className="w-3 h-3 mr-1" />
                              Koblet
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <PositionIcon className="w-4 h-4 text-[#94A3B8]" />
                          <span className="text-white">{employee.position_title_no}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-[#23F7DD] font-mono font-bold">
                          {employee.hourly_rate} NOK
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[employee.status]} border`}>
                          {STATUS_LABELS[employee.status] || employee.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {employee.contract_signed ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Signert
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                            <Clock className="w-3 h-3 mr-1" />
                            Ikke signert
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openContractModal(employee)}
                            className="text-[#94A3B8] hover:text-white hover:bg-white/10"
                            title="Se kontrakt"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          {!employee.platform_user_id && employee.status === "pending" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendInvitation(employee)}
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                              title="Send invitasjon"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(employee)}
                            className="text-[#94A3B8] hover:text-white hover:bg-white/10"
                            title="Rediger"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {user?.role === "platform_admin" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteEmployee(employee)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              title="Slett"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Employee Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#23F7DD]" />
              Legg til intern ansatt
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8]">Fullt navn *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  placeholder="Ola Nordmann"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">E-post *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  placeholder="ola@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8]">Telefon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  placeholder="+47 XXX XX XXX"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">Fødselsnummer / D-nummer</Label>
                <Input
                  value={formData.personal_id}
                  onChange={(e) => setFormData({ ...formData, personal_id: e.target.value })}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  placeholder="12345678901"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#94A3B8]">Adresse</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-1 bg-black/50 border-white/10 text-white"
                placeholder="Gateadresse 123, 0123 Oslo"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8]">Stilling *</Label>
                <Select
                  value={formData.position_key}
                  onValueChange={handlePositionChange}
                >
                  <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {positions.map((pos) => (
                      <SelectItem key={pos.key} value={pos.key} className="text-white">
                        {pos.title_no} - {pos.default_rate > 0 ? `${pos.default_rate} NOK` : "Custom"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#94A3B8]">Timesats (NOK) *</Label>
                <Input
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8]">Kontraktstype</Label>
                <Select
                  value={formData.contract_type}
                  onValueChange={(v) => setFormData({ ...formData, contract_type: v })}
                >
                  <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="Permanent">Fast ansatt</SelectItem>
                    <SelectItem value="Temporary">Midlertidig</SelectItem>
                    <SelectItem value="Project-based">Prosjektbasert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#94A3B8]">Startdato</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#94A3B8]">Notater</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-1 bg-black/50 border-white/10 text-white"
                placeholder="Eventuelle tilleggsnotater..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              className="border-white/20 text-white"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleAddEmployee}
              disabled={saving}
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#23F7DD]/80"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Opprett ansatt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#23F7DD]" />
              Rediger ansatt
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Same form fields as Add Modal */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8]">Fullt navn *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">E-post *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8]">Stilling</Label>
                <Select
                  value={formData.position_key}
                  onValueChange={handlePositionChange}
                >
                  <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {positions.map((pos) => (
                      <SelectItem key={pos.key} value={pos.key} className="text-white">
                        {pos.title_no} - {pos.default_rate > 0 ? `${pos.default_rate} NOK` : "Custom"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#94A3B8]">Timesats (NOK)</Label>
                <Input
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#94A3B8]">Status</Label>
              <Select
                value={formData.status || selectedEmployee?.status || "pending"}
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="pending">Venter</SelectItem>
                  <SelectItem value="invited">Invitert</SelectItem>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="contract_signed">Kontrakt signert</SelectItem>
                  <SelectItem value="terminated">Avsluttet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
              className="border-white/20 text-white"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleUpdateEmployee}
              disabled={saving}
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#23F7DD]/80"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Lagre endringer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Platform User Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-[#23F7DD]" />
              Inviter fra plattformen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-[#94A3B8]">Søk etter bruker</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  value={searchPlatformUsers}
                  onChange={(e) => {
                    setSearchPlatformUsers(e.target.value);
                    searchPlatformUsersHandler(e.target.value);
                  }}
                  className="pl-10 bg-black/50 border-white/10 text-white"
                  placeholder="Søk på navn eller e-post..."
                />
              </div>
            </div>

            {searchingUsers && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-[#23F7DD]" />
              </div>
            )}

            {platformUsers.length > 0 && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {platformUsers.map((user) => (
                  <div
                    key={user.user_id}
                    className={`p-3 rounded-lg border ${
                      user.is_internal_employee
                        ? "border-white/5 bg-white/5 opacity-50"
                        : "border-white/10 bg-white/5 hover:border-[#23F7DD]/30 cursor-pointer"
                    }`}
                    onClick={() => !user.is_internal_employee && handleInvitePlatformUser(user)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-sm text-[#64748B]">{user.email}</p>
                      </div>
                      {user.is_internal_employee ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          Allerede ansatt
                        </Badge>
                      ) : (
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          {user.role === "team_leader" ? "Lagleder" : "Worker"}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-white/10 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#94A3B8]">Stilling</Label>
                  <Select
                    value={formData.position_key}
                    onValueChange={handlePositionChange}
                  >
                    <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      {positions.map((pos) => (
                        <SelectItem key={pos.key} value={pos.key} className="text-white">
                          {pos.title_no}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#94A3B8]">Timesats (NOK)</Label>
                  <Input
                    type="number"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-black/50 border-white/10 text-white"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteModal(false)}
              className="border-white/20 text-white"
            >
              Lukk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Preview Modal */}
      <Dialog open={showContractModal} onOpenChange={setShowContractModal}>
        <DialogContent className="bg-white text-black max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-black">
              <FileText className="w-5 h-5" />
              Arbeidskontrakt - {selectedEmployee?.name}
            </DialogTitle>
          </DialogHeader>

          {selectedEmployee && (
            <ContractPreview
              employee={selectedEmployee}
              position={positions.find((p) => p.key === selectedEmployee.position_key)}
            />
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowContractModal(false)}
            >
              Lukk
            </Button>
            <Button
              onClick={() => window.print()}
              className="bg-black text-white hover:bg-black/80"
            >
              <Download className="w-4 h-4 mr-2" />
              Skriv ut / Last ned PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

// Contract Preview Component
function ContractPreview({ employee, position }) {
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="contract-preview p-6 text-sm" style={{ lineHeight: 1.5 }}>
      <h2 className="text-xl font-bold mb-4">STANDARD EMPLOYMENT CONTRACT</h2>
      <p className="font-bold">AllStillas Holding AS, Org.nr: 932 489 473</p>
      <p>Brobekkveien 100, 0582 Oslo</p>
      <p><strong>Warehouse:</strong> Brobekkveien 100, 0582 Oslo</p>

      <hr className="my-4" />

      <h3 className="font-bold mt-4 mb-2">1. PARTIES</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p><strong>Employer</strong></p>
          <p>AllStillas Holding AS (Org.nr: 932 489 473)</p>
          <p>Brobekkveien 100, 0582 Oslo</p>
        </div>
        <div>
          <p><strong>Employee</strong></p>
          <p><strong>Name:</strong> {employee.name || "{{Full Name}}"}</p>
          <p><strong>Personal ID:</strong> {employee.personal_id || "{{ID}}"}</p>
          <p><strong>Address:</strong> {employee.address || "{{Address}}"}</p>
          <p><strong>Phone:</strong> {employee.phone || "{{Phone}}"}</p>
          <p><strong>Email:</strong> {employee.email || "{{Email}}"}</p>
        </div>
      </div>

      <h3 className="font-bold mt-4 mb-2">2. EMPLOYMENT DETAILS</h3>
      <p><strong>Start date:</strong> {employee.start_date || "{{Start Date}}"}</p>
      <p><strong>Contract date:</strong> {today}</p>
      <p><strong>Contract type:</strong> {employee.contract_type || "Permanent"}</p>
      <p><strong>Place of work:</strong> {employee.place_of_work || "Oslo and surrounding areas"}</p>

      <h3 className="font-bold mt-4 mb-2">3. POSITION AND RESPONSIBILITIES</h3>
      <p><strong>Position:</strong> {position?.title || employee.position_key}</p>
      <p><strong>Role expectations:</strong></p>
      <ul className="list-disc ml-6 mt-2">
        {position?.responsibilities?.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
      {position?.limitations?.length > 0 && (
        <>
          <p className="mt-2"><strong>Limitations:</strong></p>
          <ul className="list-disc ml-6">
            {position?.limitations?.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </>
      )}

      <h3 className="font-bold mt-4 mb-2">4. WORKING HOURS</h3>
      <p><strong>Normal working time:</strong> 37.5 hours per week.</p>
      <p><strong>Ordinary working hours:</strong> {employee.work_hours || "07:00–15:00"}</p>

      <h3 className="font-bold mt-4 mb-2">5. SALARY AND PAYMENTS</h3>
      <p><strong>Hourly rate:</strong> {employee.hourly_rate} NOK/hour</p>
      <p><strong>Payment date:</strong> 5th of each month</p>
      <p><strong>Overtime:</strong> 50% (weekday); 100% (Sundays/holidays)</p>
      <p><strong>Pension (OTP):</strong> 2%</p>

      <h3 className="font-bold mt-4 mb-2">6. HOLIDAY</h3>
      <p><strong>Holiday entitlement:</strong> 4 weeks + 1 day</p>
      <p><strong>Holiday pay:</strong> 10.2%</p>

      <h3 className="font-bold mt-4 mb-2">7. TRIAL PERIOD</h3>
      <p><strong>Trial period:</strong> 6 months</p>
      <p><strong>Notice during trial:</strong> 14 days</p>

      <h3 className="font-bold mt-4 mb-2">8. TERMINATION</h3>
      <p>After trial period: <strong>3 months mutual notice</strong></p>

      {employee.notes && (
        <>
          <h3 className="font-bold mt-4 mb-2">ADDITIONAL NOTES</h3>
          <p>{employee.notes}</p>
        </>
      )}

      <hr className="my-6" />

      <p><strong>Date:</strong> {today} &nbsp;&nbsp; <strong>Place:</strong> Oslo</p>

      <div className="grid grid-cols-2 gap-8 mt-6">
        <div className="border border-dashed border-gray-400 rounded-lg p-4 min-h-[100px]">
          <strong>Employer Signature</strong>
          <p className="text-gray-500 mt-4">Name / Title:</p>
          <p className="text-gray-500">Signature:</p>
        </div>
        <div className="border border-dashed border-gray-400 rounded-lg p-4 min-h-[100px]">
          <strong>Employee Signature</strong>
          <p className="text-gray-500 mt-4">Name:</p>
          <p className="text-gray-500">Signature:</p>
        </div>
      </div>
    </div>
  );
}
